'use client';
import { Chip } from '@mui/material';
import { Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { useLocale } from 'next-intl';
import { useTranslations } from 'next-intl';

import {
  Wrapper,
  Content,
  Text,
  Badges,
  Timeline,
  TimelineItem,
} from './Banner.styled';
import Button from '@/ui/components/Button';
import Image from '@/ui/components/Image';
import Link from '@/ui/components/Link';
import { log } from '@/utils/logger';
import { urlBuilder } from '@/utils/urlBuilder';
import { addLocalePath } from '@/utils/urlBuilder/utils';

const messages = {
  en: {
    one: '{count} chapter',
    other: '{count} chapters',
  },
  de: {
    one: '{count} Kapitel',
    other: '{count} Kapitel',
  },
  fr: {
    one: '{count} chapitre',
    other: '{count} chapitres',
  },
  es: {
    one: '{count} capítulo',
    other: '{count} capítulos',
  },
  it: {
    one: '{count} capitolo',
    other: '{count} capitoli',
  },
  ja: {
    one: '{count} 章',
    other: '{count} 章',
  },
  pl: {
    one: '{count} rozdział',
    few: '{count} rozdziały',
    many: '{count} rozdziałów',
    other: '{count} rozdziałów',
  },
  cs: {
    one: '{count} kapitola',
    few: '{count} kapitoly',
    many: '{count} kapitol',
    other: '{count} kapitol',
  },
};

const ChaptersCount = ({ count }) => {
  const locale = useLocale();
  const pluralRule = new Intl.PluralRules(locale).select(count);

  if (!count) {
    return null;
  }

  return (
    <Chip label={messages[locale][pluralRule].replace('{count}', count)} />
  );
};

interface DifficultyResult {
  numeric: number;
  label: string;
}

interface AssemblyGuideBannerProps {
  item: CategoryData;
  chapters: GuideData[];
  difficulty: DifficultyResult;
}

// function selectEightChapters(chapters: GuideData[]): GuideData[] {
//   const length = chapters.length;

//   // If we have 8 or fewer chapters, return all of them
//   if (length <= 8) {
//     return chapters;
//   }

//   // For more than 8 chapters, we'll select exactly 8
//   const result: GuideData[] = [];

//   // First chapter
//   result.push(chapters[0]);

//   // Calculate indices for 6 middle chapters
//   const gap = (length - 1) / 7; // -1 because we're starting from 0, divide by 7 for 8 points total

//   // Add 6 middle chapters
//   for (let i = 1; i <= 6; i++) {
//     const index = Math.floor(gap * i);
//     if (index < length - 1) {
//       // Ensure we don't pick the last chapter
//       result.push(chapters[index]);
//     }
//   }

//   // Last chapter
//   result.push(chapters[length - 1]);

//   return result;
// }

export default async function AssemblyGuideBanner({
  item,
  chapters,
  difficulty,
}: AssemblyGuideBannerProps) {
  const t = useTranslations();
  const locale = useLocale();
  const firstChapter = item?.chapters?.[0] || null;
  // const displayedChapters = selectEightChapters(chapters);

  if (!item) {
    log.error('No item provided to AssemblyGuideBanner');
    return null;
  }

  if (!item.chapters) {
    log.error('No chapters provided to AssemblyGuideBanner');
    return null;
  }

  return (
    <Wrapper>
      <Timeline>
        {chapters.map(chapter => (
          <Tooltip key={chapter.id} title={chapter.title} placement="top" arrow>
            <TimelineItem>
              <Link href={urlBuilder({ item: chapter, locale })}>
                <Image
                  key={chapter.id}
                  src={chapter.media.image.thumbnail}
                  alt={chapter.title}
                  width={66}
                  height={66}
                  style={{ display: 'block' }}
                />
              </Link>
            </TimelineItem>
          </Tooltip>
        ))}
      </Timeline>
      <Content>
        <Text>
          <Typography variant="h5" component="h3">
            {item.title}
            {item?.meta?.manual_version?.[0] && <> ({item?.meta?.manual_version?.[0]})</>}
          </Typography>
          <Badges>
            <ChaptersCount count={chapters.length} />
            <Chip label={t('guideCategory-difficulty-' + difficulty.label.toLowerCase())} />
          </Badges>
        </Text>
        {firstChapter && (
          <Button href={addLocalePath(locale, 'manual/' + item?.slug)}>{t('guideCategory-begin-assembly')}</Button>
        )}
      </Content>
    </Wrapper>
  );
}
